import { OrderCatalogProductAdditionalEntity } from "../../Objects/Order/OrderCatalogProductAdditionalEntity"
import { ICartItem } from "../../Interfaces/ICardItem"
import { IProductRequest } from "../../Interfaces/IProductRequest"
import { addtionalMemory, categoryMemory, comboMemory, companyMemory, catalogItemsMemory, productsMemory } from "../../Objects/Memory/Memory"
import { CatalogProductEntity } from "../../Objects/ProductsAndCatalogItems/CatalogProductEntity"
import { CustomerEntity } from "../../Objects/Customer/CustomerEntity"
import { CalculatePromotions } from "../../Objects/Calculates/CalculatePromotions"
import { EComboItemType } from "../../Enum/EComboItemType"
import { ISelectedSelectionRequest } from "../../Interfaces/ISelectionRequest"

const FALLBACK_CATEGORY_ICON = "🛍️"

export const PUBLIC_MENU_COMBO_CATEGORY_ID = -9900

const COMBO_CATEGORY_NAME = "combos"

const getCurrentProductPrice = (catalogProduct: CatalogProductEntity, customer?: CustomerEntity) => {
  const baseValue = Number(catalogProduct.valueBase)

  if (!customer || !catalogProduct.promotions || catalogProduct.promotions.length === 0) {
    return {
      price: baseValue,
      oldPrice: undefined,
    }
  }

  const calculatePromotion = new CalculatePromotions({
    customer,
    productsAndCatalogItems: catalogProduct,
    addtionals: [],
    timeZoneId: companyMemory.timeZoneId,
  })

  const finalValue = Number(calculatePromotion.valueFinal)

  return {
    price: finalValue,
    oldPrice: finalValue < baseValue ? baseValue : undefined,
  }
}

export function buildAllProducts(customer?: CustomerEntity): IProductRequest[] {
  const comboCategory = categoryMemory.listData.find(
    (category) => category.name.trim().toLowerCase() === COMBO_CATEGORY_NAME,
  )

  const addtionals = addtionalMemory.listData.map((add) => ({
    id: add.id,
    name: add.name,
    image: add.iconUrl,
    price: Number(add.value),
    quantity: 1,
  }))

  const products: IProductRequest[] = productsMemory.listData.map((product) => {
    const catalogProduct: CatalogProductEntity = product.catalogProductEntity

    const currentPrice = getCurrentProductPrice(catalogProduct, customer)

    const freeAllowedIds = catalogProduct.freeAllowedAddtionals?.map((e) => e.id) ?? []

    const availableAddOns = addtionals
      .filter((e) => catalogProduct.allowedAddtionals.map((e) => e.id).includes(e.id))
      .map((e) => {
        const productAdditional = catalogProduct.allowedAddtionals.find((additional) => additional.id === e.id)

        return {
          ...e,
          isFreeEligible: freeAllowedIds.includes(e.id),
          hasStockControl: productAdditional?.hasStockControl ?? false,
          stockQuantity: productAdditional?.stockQuantity ?? 0,
          isOutOfStock: productAdditional?.isOutOfStock ?? false,
        }
      })

    return {
      id: `product-${product.id}`,
      name: catalogProduct.name,
      description: catalogProduct.description,
      price: currentPrice.price,
      categoryId: catalogProduct.categoryId,
      image: catalogProduct.iconUrl,
      images: catalogProduct.imageUrls,
      oldPrice: currentPrice.oldPrice,
      categoryIcon: catalogProduct.category?.icon ?? FALLBACK_CATEGORY_ICON,
      badges: catalogProduct.tags,
      hasPreparationTime: catalogProduct.hasPreparationTime,
      preparationTimeMinutes: catalogProduct.preparationTimeMinutes,
      availableAddOns: availableAddOns,
      freeAddtionalsLimit: catalogProduct.freeAddtionalsLimit,
      productOrderMode: catalogProduct.productOrderMode,
      reservationAllowedDays: catalogProduct.reservationAllowedDays,
      reservationAllowedTimes: catalogProduct.reservationAllowedTimes,
      availableSelections: (catalogProduct.allowedSelections ?? []).map((selection) => ({
        id: selection.id,
        name: selection.name,
        options: selection.options.map((option) => ({
          id: option.id,
          name: option.name,
          image: option.iconUrl,
          value: option.value,
          isFree: option.isFree,
          allowMultiple: option.allowMultiple,
          maximumSelectionsLimit: option.maximumSelectionsLimit,
          isOutOfStock: option.isOutOfStock,
          hasStockControl: option.hasStockControl,
          stockQuantity: option.stockQuantity,
        })),
        allowMultiple: selection.allowMultiple,
        isRequired: selection.isRequired,
        requiredOptionsCount: selection.requiredOptionsCount,
        freeOptionsLimit: selection.freeOptionsLimit,
        hasMaximumSelectionsLimit: selection.hasMaximumSelectionsLimit,
        maximumSelectionsLimit: selection.maximumSelectionsLimit,
      })),
    }
  })

  const catalogItems = catalogItemsMemory.listData.map((catalogItem) => {
    const catalogProduct = catalogItem.catalogProductEntity

    const currentPrice = getCurrentProductPrice(catalogProduct, customer)

    const freeAllowedIds = catalogProduct.freeAllowedAddtionals?.map((e) => e.id) ?? []

    const availableAddOns = addtionals
      .filter((e) => catalogProduct.allowedAddtionals.map((e) => e.id).includes(e.id))
      .map((e) => {
        const productAdditional = catalogProduct.allowedAddtionals.find((additional) => additional.id === e.id)

        return {
          ...e,
          isFreeEligible: freeAllowedIds.includes(e.id),
          hasStockControl: productAdditional?.hasStockControl ?? false,
          stockQuantity: productAdditional?.stockQuantity ?? 0,
          isOutOfStock: productAdditional?.isOutOfStock ?? false,
        }
      })

    return {
      id: `catalogItem-${catalogItem.id}`,
      name: catalogProduct.name,
      description: catalogProduct.description,
      price: currentPrice.price,
      categoryId: catalogProduct.categoryId,
      image: catalogProduct.iconUrl,
      images: catalogProduct.imageUrls,
      oldPrice: currentPrice.oldPrice,
      categoryIcon: catalogProduct.category?.icon ?? FALLBACK_CATEGORY_ICON,
      badges: catalogProduct.tags,
      hasPreparationTime: catalogProduct.hasPreparationTime,
      preparationTimeMinutes: catalogProduct.preparationTimeMinutes,
      availableAddOns: availableAddOns,
      freeAddtionalsLimit: catalogProduct.freeAddtionalsLimit,
      productOrderMode: catalogProduct.productOrderMode,
      reservationAllowedDays: catalogProduct.reservationAllowedDays,
      reservationAllowedTimes: catalogProduct.reservationAllowedTimes,
      availableSelections: (catalogProduct.allowedSelections ?? []).map((selection) => ({
        id: selection.id,
        name: selection.name,
        options: selection.options.map((option) => ({
          id: option.id,
          name: option.name,
          image: option.iconUrl,
          value: option.value,
          isFree: option.isFree,
          allowMultiple: option.allowMultiple,
          maximumSelectionsLimit: option.maximumSelectionsLimit,
          isOutOfStock: option.isOutOfStock,
          hasStockControl: option.hasStockControl,
          stockQuantity: option.stockQuantity,
        })),
        allowMultiple: selection.allowMultiple,
        isRequired: selection.isRequired,
        requiredOptionsCount: selection.requiredOptionsCount,
        freeOptionsLimit: selection.freeOptionsLimit,
        hasMaximumSelectionsLimit: selection.hasMaximumSelectionsLimit,
        maximumSelectionsLimit: selection.maximumSelectionsLimit,
      })),
    }
  })

  const combos = comboMemory.listData.map((combo) => {
    const catalogProduct = combo.catalogProductEntity

    const currentPrice = getCurrentProductPrice(catalogProduct, customer)

    const freeAllowedIds = catalogProduct.freeAllowedAddtionals?.map((e) => e.id) ?? []

    const availableAddOns = addtionals
      .filter((e) => catalogProduct.allowedAddtionals.map((e) => e.id).includes(e.id))
      .map((e) => {
        const productAdditional = catalogProduct.allowedAddtionals.find((additional) => additional.id === e.id)

        return {
          ...e,
          isFreeEligible: freeAllowedIds.includes(e.id),
          hasStockControl: productAdditional?.hasStockControl ?? false,
          stockQuantity: productAdditional?.stockQuantity ?? 0,
          isOutOfStock: productAdditional?.isOutOfStock ?? false,
        }
      })

    const comboItemsPreview = combo.items.map((item) => {
      const sourceName =
        item.itemType === EComboItemType.Product
          ? productsMemory.findById(item.itemId)?.catalogProductEntity?.name
          : catalogItemsMemory.findById(item.itemId)?.catalogProductEntity?.name

      const prefix = item.itemType === EComboItemType.Product ? "🍽️" : "🥤"
      const quantityLabel = item.quantity > 1 ? ` x${item.quantity}` : ""

      return `${prefix} ${sourceName ?? `Item #${item.itemId}`}${quantityLabel}`
    })

    return {
      id: `combo-${combo.id}`,
      linkedProductId: combo.productId,
      name: catalogProduct.name,
      description: catalogProduct.description,
      price: currentPrice.price,
      categoryId: comboCategory?.id ?? PUBLIC_MENU_COMBO_CATEGORY_ID,
      image: catalogProduct.iconUrl,
      images: catalogProduct.imageUrls,
      oldPrice: currentPrice.oldPrice,
      categoryIcon: catalogProduct.category?.icon ?? FALLBACK_CATEGORY_ICON,
      badges: ["Combo", ...(catalogProduct.tags ?? [])],
      hasPreparationTime: catalogProduct.hasPreparationTime,
      preparationTimeMinutes: catalogProduct.preparationTimeMinutes,
      availableAddOns: availableAddOns,
      freeAddtionalsLimit: catalogProduct.freeAddtionalsLimit,
      productOrderMode: catalogProduct.productOrderMode,
      reservationAllowedDays: catalogProduct.reservationAllowedDays,
      reservationAllowedTimes: catalogProduct.reservationAllowedTimes,
      availableSelections: (catalogProduct.allowedSelections ?? []).map((selection) => ({
        id: selection.id,
        name: selection.name,
        options: selection.options.map((option) => ({
          id: option.id,
          name: option.name,
          image: option.iconUrl,
          value: option.value,
          isFree: option.isFree,
          allowMultiple: option.allowMultiple,
          maximumSelectionsLimit: option.maximumSelectionsLimit,
          isOutOfStock: option.isOutOfStock,
          hasStockControl: option.hasStockControl,
          stockQuantity: option.stockQuantity,
        })),
        allowMultiple: selection.allowMultiple,
        isRequired: selection.isRequired,
        requiredOptionsCount: selection.requiredOptionsCount,
        freeOptionsLimit: selection.freeOptionsLimit,
        hasMaximumSelectionsLimit: selection.hasMaximumSelectionsLimit,
        maximumSelectionsLimit: selection.maximumSelectionsLimit,
      })),
      comboItemsPreview,
    }
  })

  return [...combos, ...products, ...catalogItems]
}

export function mapSelections(item: { selectedSelections: ISelectedSelectionRequest[] }) {
  return item.selectedSelections.map((selection) => ({
    selectionEntityId: selection.selectionId,
    selectionOptionEntityId: selection.optionId,
  }))
}

export function mapAddtionals(item: ICartItem): OrderCatalogProductAdditionalEntity[] {
  return item.selectedAddOns.map((add) => {
    return new OrderCatalogProductAdditionalEntity({
      addtionalEntityId: add.id,
      amount: add.quantity,
    })
  })
}
